import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

/*
 * GoodUnited / Field. Figma: 10 / Components / Field.
 * Field stacks a 16px label, the control and optional 14px helper text with an 8px gap.
 * Field/Checkbox and Field/Radio put the control and label in a row (FieldRow).
 */
function Field({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='field'
            role='group'
            className={cn('flex w-full flex-col gap-2', className)}
            {...props}
        />
    )
}

function FieldLabel({ className, ...props }: ComponentProps<typeof Label>) {
    return (
        <Label
            data-slot='field-label'
            className={cn('w-fit', className)}
            {...props}
        />
    )
}

function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
    return (
        <p
            data-slot='field-description'
            className={cn('text-sm text-text-secondary', className)}
            {...props}
        />
    )
}

function FieldError({ className, children, ...props }: ComponentProps<'p'>) {
    if (!children) return null

    return (
        <p
            data-slot='field-error'
            role='alert'
            className={cn('text-sm text-status-error-foreground', className)}
            {...props}
        >
            {children}
        </p>
    )
}

interface FieldRowProps extends ComponentProps<'label'> {
    control: ReactNode
}

/* Field/Checkbox and Field/Radio: control first, 8px gap, Roboto / text-base label. */
function FieldRow({ control, className, children, ...props }: FieldRowProps) {
    return (
        <label
            data-slot='field-row'
            className={cn('flex w-fit cursor-pointer items-center gap-2 py-1 text-base text-text-primary has-data-disabled:cursor-not-allowed has-data-disabled:opacity-50', className)}
            {...props}
        >
            {control}
            <span>
                {children}
            </span>
        </label>
    )
}

/* Field/Row: a label column beside its control, used inside drawers and editors. */
function FieldInline({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='field-inline'
            role='group'
            className={cn('grid grid-cols-1 items-start gap-2 sm:grid-cols-[112px_1fr] sm:gap-4', className)}
            {...props}
        />
    )
}

export {
    Field,
    FieldDescription,
    FieldError,
    FieldInline,
    FieldLabel,
    FieldRow
}
